import { itemDef, STRUCTURE_LOOKS, STRUCTURE_SHAPES, type StructurePart } from "@eldrun/content";
import { Box3, BoxGeometry, BufferGeometry, type BufferAttribute, ConeGeometry, CylinderGeometry, DodecahedronGeometry, IcosahedronGeometry, Sphere, SphereGeometry, Vector3 } from "three";
import { MODELS, STRUCTURE_HINGES, STRUCTURE_MODELS, type ModelId } from "../assets/models.generated.ts";
import { lumpy, merge, type Piece } from "./mesh-kit.ts";
import { models, type ModelLibrary } from "./model-library.ts";

const WOOD = "#A0703F";
const DARK_WOOD = "#5C3A20";
const CLAY = "#9C5B3C";
const STONE = "#8A8578";
const IRON = "#3E444C";

const leg = (x: number, z: number, h: number): Piece => ({ geometry: new BoxGeometry(0.1, h, 0.1), color: DARK_WOOD, position: [x, h / 2, z] });

/**
 * Estações em escala de mundo (~1 m), com a base em y = 0 e a frente em +z. Low-poly e com cor
 * por vértice, como o resto da arte procedural (ADR 0007). Vento zero.
 */
const STATIONS: Record<string, () => Piece[]> = {
  bancada: () => [
    { geometry: new BoxGeometry(1.3, 0.12, 0.75), color: WOOD, position: [0, 0.86, 0] },
    leg(-0.55, -0.28, 0.8),
    leg(0.55, -0.28, 0.8),
    leg(-0.55, 0.28, 0.8),
    leg(0.55, 0.28, 0.8),
    { geometry: new BoxGeometry(1.1, 0.06, 0.06), color: DARK_WOOD, position: [0, 0.3, -0.28] },
    { geometry: new BoxGeometry(0.34, 0.08, 0.2), color: "#B9B4A8", position: [0.3, 0.96, 0.05], rotation: [0, 0.4, 0] },
    { geometry: new CylinderGeometry(0.03, 0.03, 0.4, 5), color: DARK_WOOD, position: [-0.35, 0.95, 0.1], rotation: [0, 0, Math.PI / 2] },
  ],
  "forno-barro": () => [
    { geometry: new CylinderGeometry(0.7, 0.75, 0.3, 9), color: STONE, position: [0, 0.15, 0] },
    { geometry: lumpy(new SphereGeometry(0.62, 9, 6, 0, Math.PI * 2, 0, Math.PI / 2), 0.08, 3), color: CLAY, position: [0, 0.3, 0] },
    { geometry: new BoxGeometry(0.36, 0.3, 0.2), color: "#2A1A12", position: [0, 0.45, 0.55] },
    { geometry: new CylinderGeometry(0.1, 0.12, 0.35, 6), color: CLAY, position: [0.2, 0.95, -0.15] },
  ],
  "panela-barro": () => [
    ...[0, 1, 2].map((i): Piece => ({
      geometry: lumpy(new IcosahedronGeometry(0.16, 0), 0.3, i),
      color: STONE,
      position: [Math.cos(i * 2.094) * 0.36, 0.1, Math.sin(i * 2.094) * 0.36],
    })),
    { geometry: new CylinderGeometry(0.42, 0.32, 0.42, 10), color: "#8C4A2E", position: [0, 0.42, 0] },
    { geometry: new CylinderGeometry(0.45, 0.45, 0.05, 10), color: "#6E3822", position: [0, 0.64, 0] },
    { geometry: new ConeGeometry(0.22, 0.2, 5), color: "#F28A2E", position: [0, 0.1, 0] },
  ],
  bigorna: () => [
    { geometry: new CylinderGeometry(0.32, 0.38, 0.45, 7), color: DARK_WOOD, position: [0, 0.22, 0] },
    { geometry: new BoxGeometry(0.22, 0.2, 0.3), color: IRON, position: [0, 0.55, 0] },
    { geometry: new BoxGeometry(0.75, 0.16, 0.34), color: IRON, position: [0, 0.73, 0] },
    { geometry: new ConeGeometry(0.12, 0.35, 6), color: IRON, position: [0.54, 0.75, 0], rotation: [0, 0, -Math.PI / 2] },
  ],
  "bau-madeira": () => chest("#8B5A2B", DARK_WOOD, "#D8B048"),
  // Baús naturais (F18): o mesmo baú, com a cor de onde ele está.
  "bau-acampamento": () => chest("#7A4E28", "#5C3A20", "#B89A5A"),
  "bau-engenho": () => chest("#5E3B22", "#3E2716", IRON),
  "bau-gruta": () => chest("#6B6A5E", "#4C4B42", IRON),
  "bau-destrocos": () => chest("#5E6E73", "#44535A", "#8A4B2A"),
  // Baús de Simpatia (F19): corpo pintado pela raridade; a tampa é uma peça à parte (abre).
  "bau-achado": () => chestBody("#8B6A45", "#B89A5A"),
  "bau-simpatia": () => chestBody("#3E6FA8", "#D8B048"),
  "bau-caprichado": () => chestBody("#7A4FA0", "#E8C25A"),
  "bau-encantado": () => chestBody("#C8A23A", "#FFF1B0"),
  "bau-achado:tampa": () => [chestLid("#6E5235")],
  "bau-simpatia:tampa": () => [chestLid("#2E5486")],
  "bau-caprichado:tampa": () => [chestLid("#5E3A80")],
  "bau-encantado:tampa": () => [chestLid("#A88420")],
  // Carranca (F20; reviver da F21): carranca de proa num tronco, de cara feia para espantar o mal.
  carranca: () => [
    { geometry: new CylinderGeometry(0.28, 0.34, 1.2, 8), color: DARK_WOOD, position: [0, 0.6, 0] },
    { geometry: new CylinderGeometry(0.36, 0.3, 0.9, 8), color: "#B5533C", position: [0, 1.6, 0] },
    { geometry: new BoxGeometry(0.5, 0.18, 0.3), color: "#E8D9B0", position: [0, 1.75, 0.22] },
    { geometry: new ConeGeometry(0.12, 0.35, 5), color: "#E8D9B0", position: [0, 1.5, 0.38], rotation: [Math.PI / 2, 0, 0] },
    { geometry: new ConeGeometry(0.42, 0.5, 8), color: "#2E2A24", position: [0, 2.3, 0] },
  ],
  // Pedras de Encanto (F20; desafios e chefes da F22): pedra lavrada com uma faixa colorida.
  "pedra-encanto": () => enchanted("#5FB3C9"),
  "pedra-chefe": () => enchanted("#C9443A"),
  // Altares dos Guardiões (F20; os Guardiões são da F23), um por cor.
  "santuario-verde": () => altar("#2FBF71"),
  "santuario-azul": () => altar("#2F7FE0"),
  "santuario-vermelho": () => altar("#E0413A"),
  "santuario-amarelo": () => altar("#F2C230"),
  "santuario-rosa": () => altar("#F27AB8"),
  // Lápide (F21; era a cruz de beira de estrada): laje de pedra de topo redondo num montinho de
  // pedras, com as linhas da inscrição na frente.
  cruz: () => [
    { geometry: new BoxGeometry(0.62, 0.7, 0.16), color: TOMBSTONE, position: [0, 0.55, 0] },
    // Meio cilindro deitado (eixo em z): o arco começa em π/2, que a rotação de 90° em x leva para +y.
    { geometry: new CylinderGeometry(0.31, 0.31, 0.16, 10, 1, false, Math.PI / 2, Math.PI), color: TOMBSTONE, position: [0, 0.9, 0], rotation: [Math.PI / 2, 0, 0] },
    { geometry: new BoxGeometry(0.36, 0.04, 0.02), color: ENGRAVING, position: [0, 0.86, 0.085] },
    { geometry: new BoxGeometry(0.26, 0.04, 0.02), color: ENGRAVING, position: [0, 0.74, 0.085] },
    { geometry: new DodecahedronGeometry(0.28, 0), color: STONE, position: [0, 0.1, 0], scale: [1.2, 0.5, 1] },
  ],
  // Trouxa (F21): o pano amarrado com o inventário inteiro de quem morreu.
  trouxa: () => [
    { geometry: new DodecahedronGeometry(0.42, 0), color: "#B5533C", position: [0, 0.32, 0], scale: [1.1, 0.75, 1] },
    { geometry: new ConeGeometry(0.12, 0.25, 5), color: "#8A3B2E", position: [0, 0.68, 0] },
  ],
  // Pedra Fulminada (F24, Terra Tremendo): rocha escura caída do céu, com veios de raio acesos.
  "pedra-fulminada": () => [
    { geometry: new DodecahedronGeometry(0.9, 0), color: "#2E2A33", position: [0, 0.55, 0], scale: [1.2, 0.8, 1] },
    { geometry: new DodecahedronGeometry(0.45, 0), color: "#3A3540", position: [0.55, 0.35, 0.3] },
    { geometry: new BoxGeometry(0.9, 0.06, 0.08), color: "#8FD3FF", position: [0, 0.75, 0.5] },
    { geometry: new BoxGeometry(0.06, 0.5, 0.08), color: "#8FD3FF", position: [-0.35, 0.6, 0.55] },
  ],
  // Mercador (F19; era o mascate): gente de capuz e capa curta, com a trouxa de mercadoria nas
  // costas (+z; o rosto fica para −z).
  mascate: () => [
    { geometry: new BoxGeometry(0.16, 0.8, 0.18), color: "#4A3B2A", position: [-0.12, 0.4, 0] },
    { geometry: new BoxGeometry(0.16, 0.8, 0.18), color: "#4A3B2A", position: [0.12, 0.4, 0] },
    { geometry: new BoxGeometry(0.5, 0.62, 0.28), color: "#B5533C", position: [0, 1.11, 0] },
    { geometry: new BoxGeometry(0.12, 0.55, 0.12), color: "#B5533C", position: [-0.32, 1.12, 0] },
    { geometry: new BoxGeometry(0.12, 0.55, 0.12), color: "#B5533C", position: [0.32, 1.12, 0] },
    { geometry: new SphereGeometry(0.16, 8, 6), color: "#C98E64", position: [0, 1.6, 0] },
    // Capuz: casca um pouco maior que a cabeça, recuada (o rosto aparece na frente), com a ponta
    // caindo para trás, e a capa curta sobre os ombros.
    { geometry: new SphereGeometry(0.21, 8, 6), color: HOOD, position: [0, 1.66, 0.09] },
    { geometry: new ConeGeometry(0.1, 0.24, 6), color: HOOD, position: [0, 1.76, 0.3], rotation: [1.3, 0, 0] },
    { geometry: new BoxGeometry(0.58, 0.16, 0.34), color: HOOD, position: [0, 1.42, 0] },
    { geometry: new BoxGeometry(0.46, 0.5, 0.3), color: "#7A5A3A", position: [0, 1.15, 0.3] },
  ],
};

const TOMBSTONE = "#A8A498";
const ENGRAVING = "#5E5A52";
const HOOD = "#3F5A3E";

/** Pedra de Encanto: menir com faixa e entalhe da cor (desafio azul, chefe vermelho). */
function enchanted(band: string): Piece[] {
  return [
    { geometry: new BoxGeometry(0.9, 2.2, 0.6), color: STONE, position: [0, 1.1, 0] },
    { geometry: new BoxGeometry(0.94, 0.16, 0.64), color: band, position: [0, 1.5, 0] },
    { geometry: new BoxGeometry(0.3, 0.3, 0.05), color: band, position: [0, 0.9, 0.31] },
    { geometry: new ConeGeometry(0.5, 0.4, 4), color: "#7A766C", position: [0, 2.4, 0], rotation: [0, Math.PI / 4, 0] },
  ];
}

/** Altar do Guardião: degraus de pedra e a pedra da cor por cima. */
function altar(color: string): Piece[] {
  return [
    { geometry: new CylinderGeometry(1.4, 1.6, 0.4, 8), color: STONE, position: [0, 0.2, 0] },
    { geometry: new CylinderGeometry(0.9, 1, 0.5, 8), color: "#7A766C", position: [0, 0.65, 0] },
    { geometry: new ConeGeometry(0.45, 0.9, 6), color, position: [0, 1.35, 0] },
    { geometry: new ConeGeometry(0.45, 0.5, 6), color, position: [0, 0.85, 0], rotation: [Math.PI, 0, 0] },
  ];
}

/** Corpo do Baú de Simpatia (sem a tampa), com cinta e fecho. */
function chestBody(body: string, band: string): Piece[] {
  return [
    { geometry: new BoxGeometry(0.95, 0.5, 0.6), color: body, position: [0, 0.25, 0] },
    { geometry: new BoxGeometry(0.98, 0.06, 0.63), color: band, position: [0, 0.3, 0] },
    { geometry: new BoxGeometry(0.1, 0.14, 0.04), color: band, position: [0, 0.45, 0.32] },
  ];
}

/**
 * Dobradiça da tampa procedural dos Baús de Bênção: a borda de trás do topo do corpo. A tampa tem a
 * origem nela (a camada a põe ali e gira para abrir, F48).
 */
const PROCEDURAL_HINGE = [0, 0.5, -0.315] as const;

/** Tampa com a origem na dobradiça (a borda de trás, embaixo), estendida para a frente (+z). */
function chestLid(color: string): Piece {
  return { geometry: new BoxGeometry(0.98, 0.14, 0.63), color, position: [0, 0.07, 0.315] };
}

/** Baú: corpo, tampa, cinta e fecho. */
function chest(body: string, lid: string, band: string): Piece[] {
  return [
    { geometry: new BoxGeometry(0.95, 0.5, 0.6), color: body, position: [0, 0.25, 0] },
    { geometry: new BoxGeometry(0.98, 0.14, 0.63), color: lid, position: [0, 0.57, 0] },
    { geometry: new BoxGeometry(0.98, 0.06, 0.63), color: band, position: [0, 0.3, 0] },
    { geometry: new BoxGeometry(0.1, 0.14, 0.04), color: band, position: [0, 0.45, 0.32] },
  ];
}

const PLANK = "#A0703F";
const PLANK_DARK = "#7E5530";
const TAIPA = "#9C6B45";
const STRAW = "#D9C27A";

/** Tábuas lado a lado (pisos, rampas): faixas de cor alternada dão o desenho sem textura. */
function planks(width: number, depth: number, thickness: number, count: number, y: number): Piece[] {
  const w = width / count;
  return Array.from({ length: count }, (_, i): Piece => ({
    geometry: new BoxGeometry(w - 0.02, thickness, depth),
    color: i % 2 ? PLANK_DARK : PLANK,
    position: [-width / 2 + w * (i + 0.5), y, 0],
  }));
}

/**
 * Peças da grade (F17), na origem da célula ou da aresta (`pieceCenter`): base em y = 0, paredes ao
 * longo de x (2 m), rampas e telhados subindo para −z (a frente do giro).
 */
const PIECES: Record<string, () => Piece[]> = {
  "alicerce-pedra": () => [{ geometry: lumpy(new BoxGeometry(2, 2, 2, 2, 2, 2), 0.04, 7), color: STONE, position: [0, -1, 0] }],
  "piso-tabuas": () => planks(2, 2, 0.2, 5, -0.1),
  "parede-tabuas": () => [
    ...Array.from({ length: 4 }, (_, i): Piece => ({ geometry: new BoxGeometry(2, 0.48, 0.2), color: i % 2 ? PLANK_DARK : PLANK, position: [0, 0.25 + i * 0.5, 0] })),
    { geometry: new BoxGeometry(0.12, 2, 0.24), color: DARK_WOOD, position: [-0.94, 1, 0] },
    { geometry: new BoxGeometry(0.12, 2, 0.24), color: DARK_WOOD, position: [0.94, 1, 0] },
  ],
  "parede-taipa": () => [
    { geometry: lumpy(new BoxGeometry(2, 2, 0.2, 4, 4, 1), 0.02, 11), color: TAIPA, position: [0, 1, 0] },
    { geometry: new BoxGeometry(0.14, 2, 0.26), color: DARK_WOOD, position: [-0.93, 1, 0] },
    { geometry: new BoxGeometry(0.14, 2, 0.26), color: DARK_WOOD, position: [0.93, 1, 0] },
    { geometry: new BoxGeometry(2, 0.12, 0.26), color: DARK_WOOD, position: [0, 1.94, 0] },
  ],
  "parede-porta": () => [
    { geometry: new BoxGeometry(0.5, 2, 0.2), color: PLANK, position: [-0.75, 1, 0] },
    { geometry: new BoxGeometry(0.5, 2, 0.2), color: PLANK, position: [0.75, 1, 0] },
    { geometry: new BoxGeometry(1, 0.1, 0.24), color: DARK_WOOD, position: [0, 1.95, 0] },
  ],
  "parede-porta:folha": () => [
    // Folha com a dobradiça na origem (x = 0) e 1 m de largura para +x.
    { geometry: new BoxGeometry(0.96, 1.86, 0.08), color: PLANK_DARK, position: [0.5, 0.93, 0] },
    { geometry: new BoxGeometry(0.06, 0.06, 0.12), color: IRON, position: [0.85, 0.95, 0.06] },
  ],
  "rampa-tabuas": () => {
    // Rampa de 2 m subindo 2 m para −z: as tábuas inclinadas a 45°, mais as laterais.
    const len = Math.hypot(2, 2);
    return [
      ...planks(2, len, 0.12, 5, 0).map((p): Piece => ({ ...p, position: [p.position![0], 1, 0], rotation: [Math.PI / 4, 0, 0] })),
      { geometry: new BoxGeometry(0.1, 0.1, len), color: DARK_WOOD, position: [-0.95, 0.95, 0], rotation: [Math.PI / 4, 0, 0] },
      { geometry: new BoxGeometry(0.1, 0.1, len), color: DARK_WOOD, position: [0.95, 0.95, 0], rotation: [Math.PI / 4, 0, 0] },
    ];
  },
  "telhado-palha": () => [
    { geometry: new BoxGeometry(2.2, 0.18, 2.3), color: STRAW, position: [0, 0.5, 0], rotation: [0.45, 0, 0] },
    { geometry: new BoxGeometry(2.2, 0.1, 0.12), color: DARK_WOOD, position: [0, 1, -1.02] },
  ],
  estacada: () =>
    Array.from({ length: 7 }, (_, i): Piece => ({
      geometry: new ConeGeometry(0.13, 1.9 + (i % 2) * 0.2, 5),
      color: i % 2 ? DARK_WOOD : PLANK,
      position: [-0.86 + i * 0.287, 0.95 + (i % 2) * 0.1, 0],
    })),
};

const cache = new Map<string, BufferGeometry>();

/** Malha procedural (estações, peças da grade, a folha da porta e as tampas): a reserva dos modelos. */
function proceduralGeometry(itemId: string): BufferGeometry {
  let g = cache.get(itemId);
  if (!g) {
    const build = STATIONS[itemId] ?? PIECES[itemId];
    g = merge(build ? build() : [{ geometry: new BoxGeometry(0.8, 0.8, 0.8), color: itemDef(itemId)?.color ?? "#FF00FF", position: [0, 0.4, 0] }]);
    g.computeBoundingBox();
    cache.set(itemId, g);
  }
  return g;
}

/** LOD das estruturas (F48): 0 de perto; 1 nas peças da grade além de `STRUCTURE_LOD_DISTANCE`. */
export type StructureLod = 0 | 1;

/** Modelo (F48) da forma da estrutura ou de uma parte dela (`<forma>:<parte>`), se o pipeline tem. */
function modelOf(itemId: string, part?: StructurePart): ModelId | undefined {
  const look = STRUCTURE_LOOKS[itemId];
  return look && STRUCTURE_MODELS[part ? `${look.shape}:${part}` : look.shape];
}

/**
 * A estrutura usa o modelo com esta biblioteca: o corpo e todas as partes dele carregaram (senão, a
 * procedural inteira, com a tampa de antes).
 */
export function structureModeled(itemId: string, library: ModelLibrary = models.library): boolean {
  const look = STRUCTURE_LOOKS[itemId];
  if (!look) return false;
  const parts: readonly StructurePart[] = (STRUCTURE_SHAPES[look.shape] as { parts?: readonly StructurePart[] }).parts ?? [];
  return [undefined, ...parts].every((part) => {
    const id = modelOf(itemId, part);
    return id !== undefined && library.get(id, 0) !== undefined;
  });
}

/** LOD que o modelo tem: o pedido, ou o último (as estações e os baús têm um só). */
const levelOf = (id: ModelId, lod: StructureLod): number => Math.min(lod, MODELS[id].tris.length - 1);

/** Geometria do modelo no LOD, com as ferragens na cor do baú (a tampa tinge pela média do corpo). */
function modelGeometry(itemId: string, library: ModelLibrary, lod: StructureLod, part?: StructurePart): BufferGeometry | undefined {
  const id = modelOf(itemId, part);
  if (!id) return undefined;
  const tint = STRUCTURE_LOOKS[itemId]!.tint;
  return tint ? library.tinted(id, levelOf(id, lod), tint, false, modelOf(itemId)!) : library.get(id, levelOf(id, lod));
}

/**
 * Vistas das estruturas com modelo, por biblioteca: dividem os buffers do modelo e têm as próprias
 * caixas (o corpo do baú conta a tampa fechada), sem mexer na geometria da biblioteca.
 */
const views = new WeakMap<ModelLibrary, Map<string, BufferGeometry>>();

function cachedView(library: ModelLibrary, key: string, make: () => BufferGeometry): BufferGeometry {
  let cache = views.get(library);
  if (!cache) views.set(library, (cache = new Map()));
  let view = cache.get(key);
  if (!view) cache.set(key, (view = make()));
  return view;
}

const boxOf = (geometry: BufferGeometry): Box3 => new Box3().setFromBufferAttribute(geometry.getAttribute("position") as BufferAttribute);

/** Vista que divide atributos e índices de `source`, com a caixa dada (ou a própria). */
function view(source: BufferGeometry, box = boxOf(source)): BufferGeometry {
  const out = new BufferGeometry();
  for (const [name, attribute] of Object.entries(source.attributes)) out.setAttribute(name, attribute);
  out.setIndex(source.index);
  out.boundingBox = box;
  out.boundingSphere = box.getBoundingSphere(new Sphere());
  return out;
}

const hingeOf = (itemId: string): readonly [number, number, number] | undefined => {
  const look = STRUCTURE_LOOKS[itemId];
  return look && STRUCTURE_HINGES[`${look.shape}:tampa`];
};

/**
 * Malha da estrutura posicionada, por `itemId`: o modelo do pacote `estruturas` (F48; bancada, bigorna,
 * piso, alicerce e baús, com as ferragens na cor do baú) ou a procedural (estações, peças da grade; a
 * folha da porta à parte). A caixa (`boundingBox`) é a do que se desenha: no baú com modelo, o corpo com
 * a tampa fechada. `lod` 1 é a malha de longe das peças da grade (a de perto, se não houver).
 */
export function structureGeometry(itemId: string, library: ModelLibrary = models.library, lod: StructureLod = 0): BufferGeometry {
  if (!structureModeled(itemId, library)) return proceduralGeometry(itemId);
  const look = STRUCTURE_LOOKS[itemId]!;
  // Sem LOD de longe, a mesma vista da de perto (o mesmo lote).
  return cachedView(library, `${look.shape}@${levelOf(modelOf(itemId)!, lod)}~${look.tint ?? ""}`, () => {
    const body = modelGeometry(itemId, library, lod)!;
    const hinge = hingeOf(itemId);
    if (!hinge) return view(body);
    // O corpo do baú conta a tampa fechada, no lugar dela.
    const lid = modelGeometry(itemId, library, 0, "tampa")!;
    const box = boxOf(lid).translate(new Vector3(...hinge)).union(boxOf(body));
    return view(body, box);
  });
}

/** Tampa à parte, que abre girando em x na dobradiça (`hinge`, no espaço do corpo; a origem da malha). */
export interface StructureLid {
  readonly geometry: BufferGeometry;
  readonly hinge: readonly [number, number, number];
}

/**
 * Ângulo (rad) da tampa aberta, para trás na dobradiça: a tampa do modelo fica no colisor (raio 0,6 m,
 * 1,2 m de altura), como a procedural de antes.
 */
export const LID_OPEN_ANGLE = 1.2;

/**
 * Tampa do baú que abre (F48): a do modelo (o baú de madeira, os naturais e os de Bênção) ou, sem
 * modelos, a procedural dos Baús de Bênção (o Baú de Madeira e os naturais têm a tampa no corpo).
 * `undefined` se a estrutura não tem tampa à parte.
 */
export function structureLid(itemId: string, library: ModelLibrary = models.library): StructureLid | undefined {
  if (structureModeled(itemId, library)) {
    const hinge = hingeOf(itemId);
    if (!hinge) return undefined;
    const look = STRUCTURE_LOOKS[itemId]!;
    return { geometry: cachedView(library, `${look.shape}:tampa~${look.tint ?? ""}`, () => view(modelGeometry(itemId, library, 0, "tampa")!)), hinge };
  }
  const id = `${itemId}:tampa`;
  return STATIONS[id] ? { geometry: proceduralGeometry(id), hinge: PROCEDURAL_HINGE } : undefined;
}

/** Maior afastamento horizontal da origem dos vértices da malha, deslocada por `offset` (m). */
function groundReach(geometry: BufferGeometry, offset: readonly [number, number, number] = [0, 0, 0]): number {
  const position = geometry.getAttribute("position");
  let r = 0;
  for (let i = 0; i < position.count; i++) r = Math.max(r, Math.hypot(position.getX(i) + offset[0], position.getZ(i) + offset[2]));
  return r;
}

/** Raios por biblioteca, como as vistas: a malha de uma estrutura muda quando os modelos carregam (F48). */
const footprints = new WeakMap<ModelLibrary, Map<string, number>>();

/**
 * Raio do contorno da malha no chão (m, F53): o maior afastamento horizontal de um vértice da origem
 * (a base), que o giro não muda. O capim some dentro dele; como sai da malha desenhada
 * (`structureGeometry` com a mesma biblioteca), acompanha a troca das estações por modelos (F48): o
 * raio é o do modelo depois que os pacotes carregam, e o da procedural antes ou sem eles. Num baú com
 * a tampa à parte, conta a tampa fechada.
 */
export function structureFootprint(itemId: string, library: ModelLibrary = models.library): number {
  let cache = footprints.get(library);
  if (!cache) footprints.set(library, (cache = new Map()));
  let r = cache.get(itemId);
  if (r === undefined) {
    r = groundReach(structureGeometry(itemId, library));
    const lid = structureLid(itemId, library);
    if (lid) r = Math.max(r, groundReach(lid.geometry, lid.hinge));
    cache.set(itemId, r);
  }
  return r;
}

export const STATION_MESH_IDS = Object.keys(STATIONS);
export const PIECE_MESH_IDS = Object.keys(PIECES);
